/* ==========================================================================
   34 MANSION 디자인 토큰
   handoff-mockup/tokens.json · styles.css 의 :root 와 1:1 로 대응합니다.
   - CSS 변수 선언은 auction34RootVars 로 만들어져 GlobalStyles 에서 :root 에 주입
   - styled 컴포넌트에서는 a34.color.purple 처럼 var() 참조를 사용
   ========================================================================== */

type TokenDef = { css: string; value: string; use?: string };

/** 키 리터럴 타입을 유지하기 위한 항등 함수 */
const group = <T extends Record<string, TokenDef>>(defs: T) => defs;

const color = group({
  bg: { css: '--c-bg', value: '#FBF7F1', use: '페이지 기본 배경' },
  surface: { css: '--c-surface', value: '#FFFDFC', use: '카드·패널 표면' },
  surfaceWarm: {
    css: '--c-surface-warm',
    value: '#F7F1EA',
    use: '정보 박스(웜)',
  },
  surfaceLilac: {
    css: '--c-surface-lilac',
    value: '#EEE6F5',
    use: '안내 박스(퍼플)',
  },
  white: { css: '--c-white', value: '#FFFFFF', use: '상품 썸네일 배경' },

  text: { css: '--c-text', value: '#2C2724', use: '본문·제목' },
  textSub: { css: '--c-text-sub', value: '#766D68', use: '보조 설명' },
  textMute: { css: '--c-text-mute', value: '#8C7F76', use: '카드 분류 라벨' },
  textFaint: { css: '--c-text-faint', value: '#B8AAA0', use: '플레이스홀더' },

  purple: { css: '--c-purple', value: '#6840A0', use: '주요 액션·선택 상태' },
  purpleDark: { css: '--c-purple-dark', value: '#4C2C78', use: '강조 텍스트' },
  gold: { css: '--c-gold', value: '#C79A45', use: '선택 밑줄·점선' },
  goldDark: { css: '--c-gold-dark', value: '#8A6A2E', use: '가격 라벨' },
  goldSoft: { css: '--c-gold-soft', value: '#A08A6B', use: 'kicker 텍스트' },
  dangerBg: { css: '--c-danger-bg', value: '#F3E6E2', use: '불가 배지 배경' },
  dangerFg: { css: '--c-danger-fg', value: '#8C4A34', use: '불가 배지 글자' },
});

const line = group({
  warm: { css: '--line-warm', value: 'rgba(145, 111, 79, 0.18)' },
  warmSoft: { css: '--line-warm-soft', value: 'rgba(145, 111, 79, 0.12)' },
  gold: { css: '--line-gold', value: 'rgba(199, 154, 69, 0.22)' },
  purple: { css: '--line-purple', value: 'rgba(104, 64, 160, 0.22)' },
});

const radius = group({
  card: { css: '--radius-card', value: '16px' },
  panel: { css: '--radius-panel', value: '30px' },
  box: { css: '--radius-box', value: '18px' },
  pill: { css: '--radius-pill', value: '999px' },
});

const shadow = group({
  panel: { css: '--shadow-panel', value: '0 12px 35px rgba(87, 58, 38, 0.08)' },
  card: {
    css: '--shadow-card',
    value: '0 16px 32px rgba(87, 58, 38, 0.12)',
    use: 'hover 시',
  },
  modal: { css: '--shadow-modal', value: '0 24px 60px rgba(87, 58, 38, 0.22)' },
});

const font = group({
  kr: {
    css: '--font-kr',
    value:
      '"Pretendard Variable", Pretendard, "SUIT Variable", SUIT, "Noto Sans KR", sans-serif',
    use: '본문 전체',
  },
  en: {
    css: '--font-en',
    value: 'Inter, "Pretendard Variable", sans-serif',
    use: '탭·숫자·브랜드명 (tabular-nums)',
  },
});

/** 데스크톱 좌우 여백 (장식 이미지가 놓이는 영역). 모바일에서는 14px 로 덮어씁니다. */
const space = group({
  shellPadX: { css: '--shell-pad-x', value: 'clamp(64px, 14vw, 260px)' },
});

const toVars = <T extends Record<string, TokenDef>>(defs: T) => {
  const vars = {} as Record<keyof T, string>;
  (Object.keys(defs) as (keyof T)[]).forEach((key) => {
    vars[key] = `var(${defs[key].css})`;
  });
  return vars;
};

/** styled 컴포넌트에서 쓰는 var() 참조 모음 */
export const a34 = {
  color: toVars(color),
  line: toVars(line),
  radius: toVars(radius),
  shadow: toVars(shadow),
  font: toVars(font),
  space: toVars(space),
};

/** :root 에 주입할 CSS 변수 선언문 */
export const auction34RootVars = (
  [color, line, radius, shadow, font, space] as Record<string, TokenDef>[]
)
  .reduce<TokenDef[]>(
    (acc, defs) => acc.concat(Object.keys(defs).map((key) => defs[key])),
    [],
  )
  .map((token) => `${token.css}: ${token.value};`)
  .join('\n    ');

/** 타이포 스케일 (tokens.json typography.scale) */
export const a34Type = {
  guideTitle: {
    size: 'clamp(26px, 5.6vw, 52px)',
    weight: 750,
    tracking: '-0.035em',
  },
  sectionTitle: { size: '19px', weight: 700, tracking: '-0.035em' },
  cardName: {
    size: 'clamp(14px, 3.6vw, 17px)',
    weight: 700,
    tracking: '-0.035em',
  },
  body: { size: '15px', lineHeight: 1.65, tracking: '-0.018em' },
  caption: { size: '13px', lineHeight: 1.55, tracking: '-0.018em' },
} as const;

/** 레이아웃 규칙 (tokens.json layout) */
export const a34Layout = {
  maxWidth: '1180px',
  shellPadXMobile: '14px',
  /** 720px 이하 = 모바일 */
  mobileBreakpoint: 720,
  gridGap: 'clamp(12px, 2vw, 22px)',
  productCards:
    'repeat(auto-fill, minmax(min(100%, clamp(148px, 31%, 360px)), 1fr))',
  minTapTarget: '44px',
} as const;

/** 미디어쿼리 헬퍼 — 분기는 mobile 한 곳뿐입니다. */
export const a34Mq = {
  mobile: `@media (max-width: ${a34Layout.mobileBreakpoint}px)`,
} as const;
